/* ============================================================
   Admin shell — top bar with brand lockup + tab nav + role pill.
   Auth gated by /api/admin/me (CF Access enforces at edge).
   Ported from design/admin2.jsx Dashboard top-bar.
   ============================================================ */
import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { I, Lockup } from "../../lib/brand";
import { HttpError, type UserPublic, adminApi } from "../../lib/api";

type AuthState =
  | { kind: "loading" }
  | { kind: "ok"; user: UserPublic }
  | { kind: "unauth" }
  | { kind: "error"; message: string };

export default function AdminShell() {
  const [state, setState] = useState<AuthState>({ kind: "loading" });

  useEffect(() => {
    adminApi
      .me()
      .then((user) => setState({ kind: "ok", user }))
      .catch((e: unknown) => {
        if (e instanceof HttpError && (e.status === 401 || e.status === 403)) {
          setState({ kind: "unauth" });
        } else {
          setState({
            kind: "error",
            message: e instanceof Error ? e.message : String(e),
          });
        }
      });
  }, []);

  if (state.kind === "loading") {
    return (
      <Chrome user={null}>
        <Centered>Checking session…</Centered>
      </Chrome>
    );
  }
  if (state.kind === "unauth") {
    return (
      <Chrome user={null}>
        <Centered>
          <p style={{ marginBottom: 12 }}>You need to sign in via Cloudflare Access.</p>
          <a href="/admin" className="btn btn-primary btn-sm">
            Open sign-in
          </a>
        </Centered>
      </Chrome>
    );
  }
  if (state.kind === "error") {
    return (
      <Chrome user={null}>
        <Centered>
          <p style={{ color: "#b91c1c" }}>Couldn't load session: {state.message}</p>
        </Centered>
      </Chrome>
    );
  }

  return (
    <Chrome user={state.user}>
      <Outlet context={state.user} />
    </Chrome>
  );
}

function Chrome({ user, children }: { user: UserPublic | null; children: ReactNode }) {
  const isAdmin = user?.role === "admin";

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      <div
        style={{
          background: "var(--field)",
          borderBottom: "1px solid var(--line)",
          position: "sticky",
          top: 0,
          zIndex: 20,
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            gap: 20,
            height: 60,
          }}
        >
          <Lockup height={22} />
          {user && (
            <div style={{ display: "flex", gap: 4, marginInlineStart: 8 }}>
              <Tab to="/admin/leads" icon={I.list} label="Leads" />
              {isAdmin && <Tab to="/admin/employees" icon={I.users} label="Employees" />}
            </div>
          )}
          <div style={{ flex: 1 }} />
          {user && (
            <>
              <span className="pill" style={{ color: "var(--text-2)" }}>
                <span className="dot" style={{ background: "var(--signal)" }} />
                {isAdmin ? "ADMIN" : (user.email.split("@")[0] || "USER").toUpperCase()}
              </span>
              <a
                href="/cdn-cgi/access/logout"
                className="btn btn-ghost btn-sm"
                style={{ width: 36, height: 36, padding: 0 }}
                aria-label="Sign out"
                title="Sign out"
              >
                {I.logout({ size: 17 })}
              </a>
            </>
          )}
        </div>
      </div>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "26px 24px 60px" }}>{children}</div>
    </div>
  );
}

function Tab({
  to,
  icon,
  label,
}: {
  to: string;
  icon: (p?: { size?: number }) => JSX.Element;
  label: string;
}) {
  return (
    <NavLink
      to={to}
      className="nav-tab"
      style={({ isActive }) => ({
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 14px",
        fontFamily: "var(--font-sans)",
        fontWeight: 600,
        fontSize: 13.5,
        cursor: "pointer",
        border: `1px solid ${isActive ? "var(--line)" : "transparent"}`,
        background: isActive ? "var(--surface)" : "transparent",
        color: isActive ? "var(--text)" : "var(--text-2)",
        borderRadius: "var(--r-sm)",
        textDecoration: "none",
      })}
    >
      {icon({ size: 16 })}
      <span>{label}</span>
    </NavLink>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: "40vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div style={{ textAlign: "center", color: "var(--text-2)", fontSize: 14 }}>{children}</div>
    </div>
  );
}
